import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { KeySquare, Crown, MonitorDown, Menu, Eraser, DatabaseBackup, ArchiveRestore, LayoutTemplate, FilePlus2, FileText, FileType2, Lock, Search, Download, Trash2, MoreHorizontal, Copy, Pencil, FolderArchive, Files, ShieldCheck, Info, Loader2, Timer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ThemeToggle } from "../components/ThemeToggle";
import { InfoWidget } from "../components/InfoWidget";
import { PasswordPrompt } from "../components/PasswordPrompt";
import { ZipDialog, RenameDialog, ClearCacheDialog } from "../components/library/LibraryDialogs";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { DesktopDialog } from "../components/library/DesktopDialog";
import { ProDialog, usePro } from "../components/ProDialog";
import { recheckLicense } from "../lib/pro";
import { listDocs, createDoc, deleteDoc, duplicateDoc, getAutosaveMinutes, setAutosaveMinutes, listTemplates, deleteTemplate, exportBackup, restoreBackup, updateTemplate, templateFolders, folderMeta, setFolderMeta } from "../lib/storage";
import { EditTemplateDialog } from "../components/editor/SmallDialogs";
import { decryptJSON } from "../lib/crypto";
import { importDocx, importPdf, importPdfPages } from "../lib/importers";
import { TemplateStrip, PdfModeDialog } from "../components/library/Templates";
import { buildBlob, download, safeName } from "../lib/exporters";
import { APP_VERSION } from "../lib/version";
import { defaultSettings } from "../lib/pages";

const SideBtn = ({ icon: Icon, label, hint, onClick, testid, primary }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${primary ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm" : "hover:bg-accent text-foreground"}`}>
    <Icon className="h-4 w-4 shrink-0" />
    <span className="flex-1"><span className="font-medium block">{label}</span>{hint && <span className={`text-[11px] ${primary ? "text-primary-foreground/75" : "text-muted-foreground"}`}>{hint}</span>}</span>
  </button>
);

const DocRow = ({ doc, checked, onCheck, onOpen, onAction }) => (
  <div className="group grid grid-cols-[28px_1fr_auto] md:grid-cols-[28px_1fr_210px_44px] items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-accent/50 transition-colors" data-testid={`doc-row-${doc.id}`}>
    <Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`doc-select-${doc.id}`} aria-label={`Select ${doc.title}`} />
    <button onClick={onOpen} className="flex items-center gap-3 min-w-0 text-left" data-testid={`doc-open-${doc.id}`}>
      <div className="relative h-10 w-8 shrink-0 rounded-[3px] border bg-card shadow-sm flex items-center justify-center">
        <FileText className="h-4 w-4 text-primary/70" />
        {doc.encrypted && <span className="absolute -right-1.5 -bottom-1.5 h-4 w-4 rounded-full bg-amber-500 text-white flex items-center justify-center" data-testid={`doc-lock-${doc.id}`}><Lock className="h-2.5 w-2.5" /></span>}
      </div>
      <div className="min-w-0">
        <div className="font-medium truncate flex items-center gap-2">{doc.title}
          {doc.encrypted && <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-500/10 rounded px-1.5 py-0.5">Protected</span>}
        </div>
        <div className="text-xs text-muted-foreground truncate">{doc.encrypted ? "Encrypted content — password required to view" : doc.excerpt || "Empty document"}</div>
      </div>
    </button>
    <div className="hidden md:block text-xs text-muted-foreground whitespace-nowrap" data-testid={`doc-edited-${doc.id}`}>Edited {formatDistanceToNow(doc.updatedAt, { addSuffix: true })}</div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`doc-menu-${doc.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={onOpen} data-testid={`doc-action-open-${doc.id}`}><FileText className="h-4 w-4 mr-2" />Open</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("rename")} data-testid={`doc-action-rename-${doc.id}`}><Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("duplicate")} data-testid={`doc-action-duplicate-${doc.id}`}><Copy className="h-4 w-4 mr-2" />Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator />
        {["docx", "pdf", "txt"].map((f) => (
          <DropdownMenuItem key={f} onClick={() => onAction(f)} data-testid={`doc-action-export-${f}-${doc.id}`}><Download className="h-4 w-4 mr-2" />Download .{f}</DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={() => onAction("zip")} data-testid={`doc-action-zip-${doc.id}`}><FolderArchive className="h-4 w-4 mr-2" />Download as ZIP</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onAction("delete")} className="text-destructive focus:text-destructive" data-testid={`doc-action-delete-${doc.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export default function Library() {
  const nav = useNavigate();
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [zipDocs, setZipDocs] = useState(null);
  const [pwAsk, setPwAsk] = useState(null);
  const [autosave, setAutosave] = useState(getAutosaveMinutes());
  const [pdfFile, setPdfFile] = useState(null);
  const pdfRef = useRef();
  const docxRef = useRef();

  const [mine, setMine] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cacheOpen, setCacheOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const lic = usePro();
  const standalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || !/^https?:$/.test(window.location.protocol));
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  const installApp = async () => {
    if (!installEvt) return toast("To install, open your browser menu and choose “Install PaperLite” (Chrome/Edge) or “Add to Home Screen” (Safari/Android).");
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };
  const [editTpl, setEditTpl] = useState(null);
  const restoreRef = useRef();
  useEffect(() => { recheckLicense(); }, []);
  const refresh = async () => { setDocs(await listDocs()); setMine(await listTemplates()); };
  useEffect(() => { refresh(); }, []);

  const shown = useMemo(() => (docs || []).filter((d) =>
    (filter === "all" || d.encrypted) && (d.title.toLowerCase().includes(q.toLowerCase()) || (d.excerpt || "").toLowerCase().includes(q.toLowerCase()))
  ), [docs, q, filter]);

  const toggle = (id, on) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
  const allOn = shown.length > 0 && shown.every((d) => sel.has(d.id));

  const newDoc = async () => { const r = await createDoc("Untitled document"); nav(`/doc/${r.id}`); };

  const onImport = (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (kind === "pdf") setPdfFile(file);
    else runImport(file, "docx");
  };

  const backup = async () => {
    const data = await exportBackup();
    download(new Blob([JSON.stringify(data)], { type: "application/json" }), `paperlite-backup-${new Date().toISOString().slice(0, 10)}.json`);
    toast.success(`Backup downloaded · ${data.documents.length} documents, ${data.templates.length} templates, ${data.signatures.length} signatures`);
  };
  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("Restoring backup…");
    try {
      const r = await restoreBackup(JSON.parse(await file.text()));
      toast.success(`Restored: ${r.added} new, ${r.updated} updated, ${r.skipped} unchanged · ${r.templates} templates · ${r.signatures} signatures`);
      refresh();
    } catch (x) {
      toast.error(x instanceof SyntaxError ? "This file is not a valid backup" : x.message);
    }
    setBusy("");
  };

  const fromTemplate = async (t) => {
    const r = await createDoc(t.id === "blank" ? "Untitled document" : t.name, t.html, JSON.parse(JSON.stringify({ ...defaultSettings(), ...(t.settings || {}) })));
    nav(`/doc/${r.id}`);
  };

  const runImport = async (file, kind) => {
    setPdfFile(null);
    setBusy(`Importing ${file.name}…`);
    try {
      const out = kind === "pages" ? await importPdfPages(file) : { html: kind === "text" ? await importPdf(file) : await importDocx(file) };
      const r = await createDoc(file.name.replace(/\.(pdf|docx)$/i, ""), out.html, out.settings);
      toast.success(`Imported ${file.name}`);
      nav(`/doc/${r.id}`);
    } catch (x) {
      toast.error(x.message || "Import failed");
    }
    setBusy("");
  };

  const getBody = (doc) => doc.encrypted
    ? new Promise((resolve) => setPwAsk({ doc, resolve }))
    : Promise.resolve(doc.body);

  const action = async (doc, a) => {
    if (a === "rename") return setRenaming(doc);
    if (a === "delete") return setConfirmDel([doc.id]);
    if (a === "zip") return setZipDocs([doc]);
    if (a === "duplicate") { await duplicateDoc(doc); toast.success("Document duplicated"); return refresh(); }
    const body = await getBody(doc);
    if (!body) return;
    setBusy(`Preparing .${a}…`);
    try { download(await buildBlob(a, body.html, body.settings), `${safeName(doc.title)}.${a}`); }
    catch (x) { toast.error(x.message || "Export failed"); }
    setBusy("");
  };

  const doDelete = async () => {
    for (const id of confirmDel) await deleteDoc(id);
    setSel((s) => new Set([...s].filter((id) => !confirmDel.includes(id))));
    setConfirmDel(null);
    toast.success("Deleted");
    refresh();
  };

  const selectedDocs = (docs || []).filter((d) => sel.has(d.id));

  const sideContent = (
    <>
        <div className="px-5 pt-6 pb-5 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><FileType2 className="h-4 w-4" /></div>
          <div><div className="font-semibold tracking-tight leading-none">PaperLite</div><div className="text-[11px] text-muted-foreground mt-1">Local word processor</div></div>
        </div>
        <div className="px-3 space-y-1">
          <SideBtn primary icon={FilePlus2} label="New document" onClick={newDoc} testid="sidebar-new-doc-btn" />
          <SideBtn icon={FileText} label="Import .pdf" hint="Edit the text, or sign exact pages" onClick={() => pdfRef.current.click()} testid="sidebar-import-pdf-btn" />
          <SideBtn icon={FileType2} label="Import .docx" hint="Keeps headings, lists, tables, images" onClick={() => docxRef.current.click()} testid="sidebar-import-docx-btn" />
          <SideBtn icon={LayoutTemplate} label="Templates" hint="Letterhead, invoice, letter, NDA…" onClick={() => document.querySelector("[data-testid=template-strip]")?.scrollIntoView({ behavior: "smooth" })} testid="sidebar-templates-btn" />
        </div>
        <div className="px-3 mt-3">
          <SideBtn icon={Crown} label={lic ? "PaperLite Pro · active" : "Get PaperLite Pro"} hint={lic ? "AI assistant unlocked" : "One-time S$9.90 · unlocks AI"} onClick={() => setProOpen(true)} testid="sidebar-pro-btn" />
        </div>
        <div className="px-5 mt-7 mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Library</div>
        <div className="px-3 space-y-1">
          <SideBtn icon={Files} label={`All documents (${docs?.length ?? 0})`} onClick={() => setFilter("all")} testid="filter-all-btn" />
          <SideBtn icon={ShieldCheck} label={`Protected (${docs?.filter((d) => d.encrypted).length ?? 0})`} onClick={() => setFilter("locked")} testid="filter-protected-btn" />
        </div>
        <div className="px-5 mt-7">
          <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5"><Timer className="h-3 w-3" />Autosave every</label>
          <select className="mt-2 w-full h-9 rounded-md border bg-background px-2 text-sm" value={autosave} data-testid="library-autosave-select"
            onChange={(e) => { setAutosave(Number(e.target.value)); setAutosaveMinutes(e.target.value); toast.success("Autosave interval updated"); }}>
            {[1, 2, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m} minute{m > 1 ? "s" : ""}</option>)}
            <option value={0}>Off (manual save only)</option>
          </select>
        </div>
        <div className="px-5 mt-7 mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Backup</div>
        <div className="px-3 space-y-1">
          <SideBtn icon={DatabaseBackup} label="Back up library" hint="Download everything as one file" onClick={backup} testid="sidebar-backup-btn" />
          <SideBtn icon={ArchiveRestore} label="Restore backup" hint="Load a backup file into this browser" onClick={() => restoreRef.current.click()} testid="sidebar-restore-btn" />
        </div>
        <div className="px-5 mt-7 mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">App</div>
        <div className="px-3 space-y-1">
          <SideBtn icon={MonitorDown} label="Install on computer" hint="Own window, works offline · .exe / .dmg" onClick={() => setDesktopOpen(true)} testid="sidebar-install-app-btn" />
          <SideBtn icon={Eraser} label="Clear cache" hint="Free temporary data, keep documents" onClick={() => setCacheOpen(true)} testid="sidebar-clear-cache-btn" />
          {/(\.preview\.emergentagent\.com|localhost)$/.test(window.location.hostname) && <SideBtn icon={KeySquare} label="Admin" hint="Free licence keys · PayNow orders" onClick={() => nav("/admin")} testid="sidebar-admin-btn" />}
        </div>
        <div className="mt-auto p-5 space-y-3">
          <div className="rounded-lg border bg-muted/50 p-3 text-[11px] leading-relaxed text-muted-foreground flex gap-2"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />Documents live only in this browser. Clearing site data deletes them — use “Back up library” regularly.</div>
          <div className="text-[11px] text-muted-foreground" data-testid="app-version">Version {APP_VERSION}</div>
        </div>
    </>
  );

  return (
    <div className="min-h-screen bg-muted/40 flex" data-testid="library-page">
      <aside className="w-64 shrink-0 border-r bg-card hidden md:flex flex-col sticky top-0 h-screen overflow-y-auto" data-testid="library-sidebar">{sideContent}</aside>

      <main className="flex-1 min-w-0 px-5 md:px-10 py-7">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center justify-between gap-3"><h1 className="text-3xl font-semibold tracking-tight">Documents</h1><div className="2xl:hidden"><ThemeToggle /></div></div>
            <p className="text-sm text-muted-foreground mt-1">Private to this browser · no account needed</p>
            <div className="grid grid-cols-4 gap-2 mt-4 md:hidden">
              <Button size="sm" variant="outline" className="px-2" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-4 w-4 mr-1" />Menu</Button>
              <Button size="sm" className="px-2" onClick={newDoc} data-testid="mobile-new-doc-btn"><FilePlus2 className="h-4 w-4 mr-1" />New</Button>
              <Button size="sm" variant="outline" className="px-2" onClick={() => pdfRef.current.click()} data-testid="mobile-import-pdf-btn">+ PDF</Button>
              <Button size="sm" variant="outline" className="px-2" onClick={() => docxRef.current.click()} data-testid="mobile-import-docx-btn">+ Word</Button>
            </div>
          </div>
          <div className="flex items-start gap-3 min-w-0 w-full 2xl:w-auto"><div className="min-w-0 flex-1"><InfoWidget /></div><div className="hidden 2xl:block"><ThemeToggle /></div></div>
        </div>

        <TemplateStrip onPick={fromTemplate} mine={mine} onEditMine={setEditTpl} onDeleteMine={async (t) => { await deleteTemplate(t.id); toast.success(`Template “${t.name}” deleted`); refresh(); }} />

        <div className="lw-card overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-card">
            <Checkbox checked={allOn} onCheckedChange={(on) => setSel(on ? new Set(shown.map((d) => d.id)) : new Set())} data-testid="select-all-checkbox" aria-label="Select all" />
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents" className="pl-9 h-9" data-testid="doc-search-input" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {sel.size > 0 && <span className="text-xs text-muted-foreground" data-testid="selected-count">{sel.size} selected</span>}
              <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => setZipDocs(selectedDocs)} data-testid="batch-zip-btn"><FolderArchive className="h-4 w-4 mr-1.5" />Download ZIP</Button>
              <Button size="sm" variant="ghost" disabled={!sel.size} onClick={() => setConfirmDel([...sel])} className="text-destructive" data-testid="batch-delete-btn"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          {docs && shown.length === 0 && (
            <div className="py-20 text-center" data-testid="library-empty">
              <FileText className="h-10 w-10 mx-auto text-muted-foreground/50" strokeWidth={1.25} />
              <p className="mt-3 font-medium">{docs.length ? "No documents match" : "No documents yet"}</p>
              <p className="text-sm text-muted-foreground mt-1">Create a new document or import a .pdf / .docx file.</p>
              {!docs.length && <Button className="mt-5" onClick={newDoc} data-testid="empty-new-doc-btn"><FilePlus2 className="h-4 w-4 mr-1.5" />New document</Button>}
            </div>
          )}
          <div data-testid="doc-list">
            {shown.map((d) => (
              <DocRow key={d.id} doc={d} checked={sel.has(d.id)} onCheck={(on) => toggle(d.id, on)} onOpen={() => nav(`/doc/${d.id}`)} onAction={(a) => action(d, a)} />
            ))}
          </div>
        </div>
        <div className="md:hidden text-[11px] text-muted-foreground mt-6">Version {APP_VERSION}</div>
      </main>

          <input ref={pdfRef} type="file" accept=".pdf,application/pdf" hidden onChange={(e) => onImport(e, "pdf")} data-testid="import-pdf-input" />
          <input ref={docxRef} type="file" accept=".docx" hidden onChange={(e) => onImport(e, "docx")} data-testid="import-docx-input" />
          <input ref={restoreRef} type="file" accept=".json,application/json" hidden onChange={restore} data-testid="restore-backup-input" />
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col overflow-y-auto" data-testid="mobile-menu" onClick={(e) => e.target.closest("button") && setMenuOpen(false)}>{sideContent}</SheetContent>
      </Sheet>
      <ClearCacheDialog open={cacheOpen} onClose={() => setCacheOpen(false)} />
      <DesktopDialog open={desktopOpen} onClose={() => setDesktopOpen(false)} canInstall={!!installEvt} installed={standalone} onInstall={installApp} />
      <ProDialog open={proOpen} onClose={() => setProOpen(false)} />
      {busy && (
        <div className="fixed bottom-5 right-5 z-50 lw-card px-4 py-3 flex items-center gap-2 text-sm" data-testid="busy-indicator"><Loader2 className="h-4 w-4 animate-spin text-primary" />{busy}</div>
      )}
      <PdfModeDialog file={pdfFile} onClose={() => setPdfFile(null)} onChoose={(mode) => runImport(pdfFile, mode)} />
      <EditTemplateDialog template={editTpl} folders={templateFolders(mine)} onClose={() => setEditTpl(null)}
        onSave={async (patch) => {
          const oldF = editTpl.category || "General";
          if (patch.category !== oldF && !mine.some((t) => (t.category || "General") === patch.category)) setFolderMeta(patch.category, folderMeta(oldF));
          await updateTemplate(editTpl, patch); toast.success("Template updated"); refresh(); }} />
      <RenameDialog doc={renaming} onClose={() => setRenaming(null)} onDone={refresh} />
      <ZipDialog docs={zipDocs} onClose={() => setZipDocs(null)} />
      <PasswordPrompt open={!!pwAsk} title={pwAsk?.doc.title}
        onCancel={() => { pwAsk?.resolve(null); setPwAsk(null); }}
        onSubmit={async (pw) => { const body = await decryptJSON(pwAsk.doc.cipher, pw); pwAsk.resolve(body); setPwAsk(null); }} />
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.length > 1 ? `${confirmDel.length} documents` : "this document"}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes it from this browser. It cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
